import { useState } from 'react';
import { STORAGE_KEYS } from '../../utils/constants';

/**
 * Stats overlay showing detection info and keyboard shortcuts.
 * Displays in top-left corner of the image viewer.
 */
export default function StatsOverlay({
  metaDoc,
  faces,
  cropBox,
  selectedPhoto,
  cropMode,
  hasAdjustments = false,
}) {
  const [showKeys, setShowKeys] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(STORAGE_KEYS.SHOW_HOTKEYS) !== 'false';
  });
  const [showLegend, setShowLegend] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(STORAGE_KEYS.SHOW_LEGEND) !== 'false';
  });
  const [hovered, setHovered] = useState(false);

  const toggleLegend = () => {
    setShowLegend((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEYS.SHOW_LEGEND, String(next));
      return next;
    });
  };

  if (!metaDoc && !selectedPhoto) return null;

  const sentinel = metaDoc?.pipeline?.sentinel || {};
  const seraph = metaDoc?.pipeline?.seraph || {};

  // Face stats
  const totalFaces = faces?.length || 0;
  const labeledFaces = faces?.filter((f) => f.person && !f.ignored)?.length || 0;
  const ignoredFaces = faces?.filter((f) => f.ignored)?.length || 0;

  // Manual names (persons added to photo but not linked to any face)
  const linkedPersonIds = new Set(
    faces?.filter((f) => f.person)?.map((f) => f.person) || []
  );
  const manualNames = (selectedPhoto?.persons || []).filter(
    (p) => !linkedPersonIds.has(p.id)
  ).length;

  // Detection model info
  const faceModel = seraph?.model || seraph?.face_model || 'unknown';
  const faceCount = seraph?.face_count ?? seraph?.faces?.length ?? totalFaces;

  // Crop info
  const cropW = cropBox ? Math.round(cropBox.x2 - cropBox.x1) : null;
  const cropH = cropBox ? Math.round(cropBox.y2 - cropBox.y1) : null;

  // Image dimensions - Sati's original_dims is authoritative (per CLAUDE.md)
  const sati = metaDoc?.pipeline?.sati;
  const imgW = sati?.original_dims?.width || selectedPhoto?.image_width;
  const imgH = sati?.original_dims?.height || selectedPhoto?.image_height;

  // Sentinel info
  const sentinelScore = sentinel?.winner_score ?? sentinel?.crop_score;

  const toggleKeys = () => {
    setShowKeys((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEYS.SHOW_HOTKEYS, String(next));
      return next;
    });
  };

  // When hidden, show only a small toggle button
  if (!showLegend) {
    return (
      <button
        className="legend-toggle-btn"
        onClick={toggleLegend}
        title="Show legend"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '4px 8px',
          fontSize: 11,
          background: 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 4,
          color: 'rgba(255,255,255,0.7)',
          cursor: 'pointer',
          zIndex: 50,
          pointerEvents: 'auto',
        }}
      >
        ?
      </button>
    );
  }

  return (
    <>
      {/* Hide button - always clickable, positioned outside the fading overlay */}
      <button
        onClick={toggleLegend}
        title="Hide legend"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '2px 6px',
          fontSize: 10,
          background: 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: 3,
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          zIndex: 50,
          pointerEvents: 'auto',
        }}
      >
        Hide
      </button>
      <div
        className="stats-overlay"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          opacity: hovered ? 0.1 : 1,
          pointerEvents: hovered ? 'none' : 'auto',
          transition: 'opacity 0.2s ease-in-out',
          top: 28,
        }}
      >
      <div className="stats-row">
        <span className="stats-label">Faces:</span>
        <span className="stats-value">{faceCount} detected</span>
      </div>
      {(labeledFaces > 0 || manualNames > 0) && (
        <div className="stats-row">
          <span className="stats-label">Tagged:</span>
          <span className="stats-value">
            {labeledFaces}/{totalFaces - ignoredFaces}
            {manualNames > 0 && (
              <span style={{ color: '#66b7ff' }}> +{manualNames} manual</span>
            )}
          </span>
        </div>
      )}
      {faceModel && faceModel !== 'unknown' && (
        <div className="stats-row">
          <span className="stats-label">Model:</span>
          <span className="stats-value">{faceModel}</span>
        </div>
      )}
      {!!cropW && !!cropH && (
        <div className="stats-row">
          <span className="stats-label">Crop:</span>
          <span className="stats-value">
            {cropW}x{cropH}
          </span>
        </div>
      )}
      {!!imgW && !!imgH && (
        <div className="stats-row">
          <span className="stats-label">Original:</span>
          <span className="stats-value">
            {imgW}x{imgH}
          </span>
        </div>
      )}
      {sentinelScore != null && (
        <div className="stats-row">
          <span className="stats-label">Score:</span>
          <span className="stats-value">{(sentinelScore * 100).toFixed(1)}%</span>
        </div>
      )}
      {sentinel?.crop?.breakdown && (
        <div className="stats-row">
          <span className="stats-label">Crop:</span>
          <span className="stats-value">
            v={sentinel.crop.breakdown.violations?.toFixed(0)} |
            inc={sentinel.crop.breakdown.relevant_included?.toFixed(0)}
          </span>
        </div>
      )}
      <div className="stats-divider" />
      <div
        className="stats-row hotkey-toggle"
        onClick={toggleKeys}
        style={{ cursor: 'pointer' }}
      >
        <span className="stats-label">Keys</span>
        <span className="stats-value">{showKeys ? 'v' : '>'}</span>
      </div>
      {showKeys && (
        <div className="hotkey-list">
          <div className="hotkey-row">
            <kbd>D</kbd> Mark done
          </div>
          <div className="hotkey-row">
            <kbd>7</kbd> Skip photo
          </div>
          <div className="hotkey-row">
            <kbd>S</kbd> Toggle crop mode
          </div>
          <div className="hotkey-row">
            <kbd>Tab</kbd> Toggle panels
          </div>
          {hasAdjustments && (
            <div className="hotkey-row">
              <kbd>\</kbd> Compare original
            </div>
          )}
          <div className="hotkey-row">
            <kbd>Esc</kbd> {cropMode ? 'Exit crop mode' : 'Deselect'}
          </div>
          {cropMode ? (
            <>
              <div className="hotkey-row">
                <kbd>R</kbd> Reset crop aspect
              </div>
              <div className="hotkey-row">
                <kbd>Q</kbd> Full frame crop
              </div>
              <div className="hotkey-row">
                <kbd>X</kbd> Flip orientation
              </div>
              <div className="hotkey-row">
                <kbd>Arrows</kbd> Nudge crop
              </div>
            </>
          ) : (
            <div className="hotkey-row">
              <kbd>Up/Down</kbd> Prev/Next image
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
}
